import React, { memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  GlobeAltIcon,
  LanguageIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { LOCALES_TRANSLATIONS } from "@gethere/common/settings";
import { setTheme } from "../../state/reducers/system";
import { RootState } from "../../state/store";
import { useLanguageDialog } from "../../contexts/LanguageContext";
import { NavigationLink } from "./NavigationLink";
import {
  DropdownMenuItem,
} from "../ui/dropdown-menu";

interface ThemeSwitcherProps {
  variant?: "icon" | "dropdown" | "mobile";
  className?: string;
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = memo(({
  variant = "icon",
  className,
}) => {
  const dispatch = useDispatch();
  const dark = useSelector((s: RootState) => s.system.theme === "dark");
  const intl = useIntl();

  const switchTheme = useCallback(() => {
    dispatch(setTheme(dark ? "light" : "dark"));
  }, [dispatch, dark]);

  const Icon = dark ? SunIcon : MoonIcon;
  const label = dark ? "Light" : "Dark";

  if (variant === "icon") {
    return (
      <Icon
        onClick={switchTheme}
        className={`h-5 w-5 stroke-gray-500 dark:stroke-zinc-400 hover:stroke-gray-900 hover:dark:stroke-zinc-100 cursor-pointer transition-colors duration-200 ${className}`}
        aria-label={`Switch to ${label.toLowerCase()} mode`}
      />
    );
  }

  if (variant === "dropdown") {
    return (
      <DropdownMenuItem
        onClick={switchTheme}
        className={`gap-3 cursor-pointer ${className}`}
      >
        <Icon className="w-3 h-3" />
        {label}
      </DropdownMenuItem>
    );
  }

  if (variant === "mobile") {
    return (
      <NavigationLink
        to="#"
        variant="mobile"
        icon={Icon}
        onClick={switchTheme}
        className={className}
      >
        {intl.formatMessage({ id: "dark_mode", defaultMessage: "Dark Mode" })}
      </NavigationLink>
    );
  }

  return null;
});

ThemeSwitcher.displayName = "ThemeSwitcher";

interface LanguageSwitcherProps {
  variant?: "icon" | "dropdown" | "mobile";
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = memo(({
  variant = "icon",
  className,
}) => {
  const locale = useSelector((s: RootState) => s.system.locale);
  const { onOpen } = useLanguageDialog();
  
  const label = LOCALES_TRANSLATIONS[locale] || locale;

  if (variant === "icon") {
    return (
      <GlobeAltIcon
        className={`h-5 w-5 stroke-gray-500 dark:stroke-zinc-400 hover:stroke-gray-900 hover:dark:stroke-zinc-100 cursor-pointer transition-colors duration-200 ${className}`}
        aria-label={`Current language: ${label}`}
        onClick={onOpen}
      />
    );
  }

  if (variant === "dropdown") {
    return (
      <DropdownMenuItem 
        onClick={onOpen} 
        className={`gap-3 cursor-pointer ${className}`}
      >
        <GlobeAltIcon className="w-3 h-3" />
        {label}
      </DropdownMenuItem>
    );
  }

  if (variant === "mobile") {
    return (
      <NavigationLink
        to="#"
        variant="mobile"
        icon={LanguageIcon}
        onClick={onOpen}
        className={className}
      >
        {label}
      </NavigationLink>
    );
  }

  return null;
});

LanguageSwitcher.displayName = "LanguageSwitcher";

interface ThemeAndLanguageControlsProps {
  variant?: "icons" | "dropdown" | "mobile";
  className?: string;
}

export const ThemeAndLanguageControls: React.FC<ThemeAndLanguageControlsProps> = memo(({
  variant = "icons",
  className,
}) => {
  if (variant === "icons") {
    return (
      <div className={`flex flex-row items-center gap-3 ${className}`}>
        <ThemeSwitcher variant="icon" />
        <LanguageSwitcher variant="icon" />
      </div>
    );
  }

  if (variant === "dropdown") {
    return (
      <div className={className}>
        <ThemeSwitcher variant="dropdown" />
        <LanguageSwitcher variant="dropdown" />
      </div>
    );
  }

  if (variant === "mobile") {
    return (
      <div className={`space-y-1 ${className}`}>
        <ThemeSwitcher variant="mobile" />
        <LanguageSwitcher variant="mobile" />
      </div>
    );
  }

  return null;
});

ThemeAndLanguageControls.displayName = "ThemeAndLanguageControls"; 